import * as Sentry from "@sentry/vue";
import { AxiosError } from "axios";
import { notifyError } from "./notify";

function axiosErrorPath(url: string): string {
  const withoutQuery = url.split("?")[0];
  if (/^https?:\/\//i.test(withoutQuery)) {
    try {
      return new URL(withoutQuery).pathname;
    } catch {
      return withoutQuery;
    }
  }
  return withoutQuery;
}

export function axiosErrorFingerprint(error: AxiosError): string[] {
  const normalizedPath = axiosErrorPath(error.config?.url ?? "").replace(
    /\/\d+/g,
    "/*",
  );
  const method = (error.config?.method ?? "unknown").toUpperCase();
  const status = String(error.response?.status ?? error.code ?? "unknown");
  return ["AxiosError", method, normalizedPath, status];
}

export class HandledError extends Error {
  originalError: any;
  constructor(message: string, originalError: any) {
    super(message);
    this.originalError = originalError;
  }
}

export function handleError(
  error: any,
  baseMessage = "Hm, something went wrong.",
) {
  const errorMessage = getErrorMessage(error);
  const userMessage = baseMessage + (errorMessage ? `\n${errorMessage}` : "");
  notifyError({ message: userMessage });
  Sentry.withScope((scope) => {
    if (error instanceof AxiosError && !error.response) {
      scope.setTag("network_error", "true");
      scope.setTag(
        "axios_retry_count",
        String(error.config?.__retryCount ?? 0),
      );
    }
    Sentry.captureException(error);
  });
  console.error(error);
  return new HandledError(userMessage, error);
}

function getErrorMessage(error: any): string | null | undefined {
  if (error instanceof AxiosError) {
    let detail = error.response?.data?.detail;
    if (Array.isArray(detail)) {
      detail = detail[0];
    }
    if (typeof detail === "string") {
      return detail;
    }
    if (typeof detail?.msg === "string") {
      return detail.msg;
    }
  }
  return error.message;
}
